import InputAdornment from '@material-ui/core/InputAdornment'
import MuiTextField, {
  TextFieldProps as Props
} from '@material-ui/core/TextField'
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme
} from '@material-ui/core/styles'
import { CSSProperties } from '@material-ui/styles'
import * as React from 'react'

import Icon, { IconList } from '../icons/Icons'

export type customProps = {
  customSize?: 'small' | 'medium' | 'large'
  readOnly?: boolean
  icon?: keyof typeof IconList,
  formContext?: any
}

export type TextFieldProps = Props & customProps

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    muiInputRoot: {
      borderRadius: 3,
      '& > svg': {
        position: 'absolute',
        right: theme.spacing(1),
        pointerEvents: 'none'
      }
    },
    muiInputRootSmall: {
      padding: theme.spacing(1.125, 1.5)
    },
    muiInputRootMedium: {
      padding: theme.spacing(1, 1.5)
    },
    muiInputRootLarge: {
      padding: theme.spacing(1.5, 1.875)
    },
    muiInputFocus: {
      border: 'inherits'
    },
    muiInputmultiline: {
      padding: 0
    },
    muiInputLabelRoot: {
      ...theme.typography.overline,
      marginBottom: theme.spacing(0.5),
      '&.shrink': {
        transform: 'inherit'
      }
    },
    MuiFormHelperTextRoot: {
      ...theme.typography.caption,
      marginTop: theme.spacing(0.5)
    },
    MuiFormHelperTextContained: {
      marginLeft: 0
    },
    muiInputLabelFormControl: {
      position: 'static'
    }
  })
)

export const TextField: React.FC<TextFieldProps> = (props) => {
  const theme = useTheme()
  const classes = useStyles(props)
  const {
    customSize,
    readOnly,
    icon,
    select,
    InputProps,
    SelectProps,
    FormHelperTextProps = {},
    ...rest
  } = props

  const getSizeInputRoot = (): string => {
    if (customSize === 'small') {
      return classes.muiInputRootSmall
    }
    if (customSize === 'large') {
      return classes.muiInputRootLarge
    }
    return classes.muiInputRootMedium
  }

  const getSizeInputInput = (): CSSProperties => {
    if (customSize === 'small') {
      return theme.typography.overline
    }
    if (customSize === 'large') {
      return theme.typography.body1
    }
    return theme.typography.body2
  }

  const inputProps = {
    readOnly,
    ...InputProps,
    classes: {
      root: classes.muiInputRoot,
      input: `${getSizeInputRoot()} ${getSizeInputInput()}`,
      focused: classes.muiInputFocus,
      multiline: classes.muiInputmultiline
    },
    notched: false,
    ...(icon &&
      !select && {
        startAdornment: (
          <InputAdornment position="start">
            <Icon iconName={icon} size="small" />
          </InputAdornment>
        )
      })
  }

  const inputLabelProps = {
    classes: {
      root: classes.muiInputLabelRoot,
      formControl: classes.muiInputLabelFormControl,
      shrink: 'shrink'
    },
    shrink: true
  }

  const formHelperTextPropsClasses = FormHelperTextProps.classes

  const formHelperTextProps = {
    ...FormHelperTextProps,
    classes: {
      root: classes.MuiFormHelperTextRoot,
      contained: classes.MuiFormHelperTextContained,
      ...formHelperTextPropsClasses
    }
  }

  const selectProps = {
    displayEmpty: true,
    IconComponent: (): React.ReactElement => (
      <Icon iconName={icon ? icon : 'ArrowBottomIcon'} size="small" />
    ),
    ...SelectProps
  }

  return (
    <MuiTextField
      rows={4}
      select={select}
      InputProps={inputProps}
      InputLabelProps={inputLabelProps}
      FormHelperTextProps={formHelperTextProps}
      SelectProps={selectProps}
      variant="outlined"
      {...rest}
    />
  )
}

export default TextField